import { useCallback } from 'react'

import {
  type FormFieldDescriptor,
  type NarrowFieldsByValueType,
  type TidalFormBridgeOptions,
  type TidalFormState,
  useTidalFormBridge,
} from '@workwave-tidal/core/form'

import { UiFramework } from './types'

import { RecurrenceSettings } from './RecurrenceSettings'
import { BulkFieldRoot } from './components/bulk/BulkFieldRoot'
import { MixedDropdown } from './components/bulk/MixedDropdown/MixedDropdown'

export type RecurrenceSettingsBulkFieldProps = {
  uiFramework?: UiFramework
  inEditMode: boolean
  testId?: string
}

export type InputValue = uui.domain.ui.forms.BulkRecurrenceSettings

export type InputProps<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = InputValue,
> = {
  recurrenceSettingsBulkFieldProps: RecurrenceSettingsBulkFieldProps
} & TidalFormBridgeOptions<InputValue, TargetField, State, AcceptedFormValue>

export function RecurrenceSettingsBulkField<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = InputValue,
>(props: InputProps<TargetField, State, AcceptedFormValue>) {
  const {
    recurrenceSettingsBulkFieldProps: { uiFramework, inEditMode, testId },
    initialValue,
    validateOn = 'change',
    ...bridgeOptions
  } = props

  const { fieldInfo } = useTidalFormBridge<InputValue, TargetField, State, AcceptedFormValue>({
    ...bridgeOptions,
    validateOn,
    initialValue,
  })

  const handleOnChange = useCallback(
    (value: uui.domain.ui.forms.RecurrenceSettings) => {
      fieldInfo.onChange({ status: 'exact', value })
    },
    [fieldInfo.onChange],
  )

  switch (fieldInfo.value.status) {
    case 'mixed': {
      return (
        <BulkFieldRoot data-testid={testId}>
          <MixedDropdown onChangeField={handleOnChange} />
        </BulkFieldRoot>
      )
    }

    case 'exact': {
      return (
        <BulkFieldRoot data-testid={testId}>
          <RecurrenceSettings
            value={fieldInfo.value.value}
            onChange={handleOnChange}
            uiFramework={uiFramework}
            inEditMode={inEditMode}
          />
        </BulkFieldRoot>
      )
    }
  }
}
